import React, { useEffect, useMemo, useState } from 'react';
import { CommonModalProps } from '@wajeez-web/components/modals';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import {
  useContentSelector,
  ContentEntityActions,
} from '@wajeez-web/stores/content';
import { Modal, CheckBox } from '@wajeez-web/components/ui';
import { Languages } from '@wajeez-web/core/utils';

interface PublishAllCollectionComponentsModalProps
  extends CommonModalProps<{
    [key: string]: ContentEntitypublishedStateOutput[];
  }> {
  contentEntityId: string;
  loading: boolean;
  languageId?: string | undefined;
  title?: string;
}

export interface ContentEntitypublishedStateInput {
  contentEntityId: string;
  published: boolean;
}

interface ContentEntitypublishedStateOutput
  extends ContentEntitypublishedStateInput {
  title: string;
}

const PublishAllCollectionComponentsModal: React.FC<
  PublishAllCollectionComponentsModalProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  contentEntityId,
  languageId,
  title,
}) => {
  const [
    contentEntitypublishedStateInput,
    setContentEntitypublishedStateInput,
  ] = useState<{ [key: string]: ContentEntitypublishedStateOutput[] }>({});

  const {
    contentParents,
    contentColections: contentColectionsObject,
    contentColectionsEnglish: contentColectionsEnglishObject,
    parentStatus,
    collectionStatus,
  } = useContentSelector((state) => state.contentEntity);
  const dispatch = useCommonDispatch();
  const languageIdGlobal = useCommonSelector(
    ({ common }) => common.settings.LanguageId
  );

  const contentColections = useMemo(() => {
    const selectedContentCollectionsObject =
      languageId === Languages.AR.toString() ||
      languageIdGlobal.toString() === Languages.AR.toString()
        ? contentColectionsObject
        : contentColectionsEnglishObject;

    return selectedContentCollectionsObject['all'] || [];
  }, [
    contentColectionsObject,
    contentColectionsEnglishObject,
    languageId,
    languageIdGlobal,
  ]);

  const handleSelectAll = (key: string, val?: boolean) => {
    setContentEntitypublishedStateInput((s) => {
      return {
        ...s,
        [key]: s[key].map((item) => {
          return {
            ...item,
            published: val || false,
          };
        }),
      };
    });
  };

  const handleSelect = (key: string, i: number, val?: boolean) => {
    setContentEntitypublishedStateInput((s) => {
      return {
        ...s,
        [key]: s[key].map((item, index) => {
          if (index === i) {
            return {
              ...item,
              published: val || false,
            };
          }
          return item;
        }),
      };
    });
  };

  const originalState = useMemo(() => {
    const newState = { ...contentEntitypublishedStateInput };
    contentParents.forEach((item) => {
      // eslint-disable-next-line no-prototype-builtins
      newState[item.contentEntityType || ''] =
        // eslint-disable-next-line no-prototype-builtins
        newState.hasOwnProperty(item.contentEntityType || '') &&
        !Object.values(newState[item.contentEntityType || '']).find(
          (ele) => ele.contentEntityId === item.id
        )
          ? [
              ...newState[item.contentEntityType || ''],
              {
                contentEntityId: item.id || '',
                published: item.published || false,
                title: item.title || '',
              },
            ]
          : [
              {
                contentEntityId: item.id || '',
                published: item.published || false,
                title: item.title || '',
              },
            ];
    });
    contentColections.forEach((item) => {
      // eslint-disable-next-line no-prototype-builtins
      newState[item.contentEntityType || ''] =
        // eslint-disable-next-line no-prototype-builtins
        newState.hasOwnProperty(item.contentEntityType || '') &&
        !Object.values(newState[item.contentEntityType || '']).find(
          (ele) => ele.contentEntityId === item.id
        )
          ? [
              ...newState[item.contentEntityType || ''],
              {
                contentEntityId: item.id || '',
                published: item.published || false,
                title: item.title || '',
              },
            ]
          : [
              {
                contentEntityId: item.id || '',
                published: item.published || false,
                title: item.title || '',
              },
            ];
    });

    return newState;
  }, [contentParents, contentColections]);

  useEffect(() => {
    dispatch(
      ContentEntityActions.getContentCollectionAsync({
        ContenteEntityId: contentEntityId,
        page: 1,
        pageSize: 1000,
        ContenteEntityType: '',
        languageId,
      })
    );
    dispatch(
      ContentEntityActions.getContentparentsAsync({
        ContenteEntityId: contentEntityId,
        page: 1,
        pageSize: 1000,
        ContenteEntityType: '',
        languageId,
      })
    );

    return () => {
      dispatch(ContentEntityActions.clearContentCollection(''));
      dispatch(ContentEntityActions.clearContentParents());
    };
  }, [contentEntityId, languageId]);

  useEffect(() => {
    setContentEntitypublishedStateInput(originalState);
  }, [originalState]);

  console.log({
    originalState,
    contentEntitypublishedStateInput,
  });

  return (
    <Modal
      loading={loading}
      title={title || ''}
      open={isOpen}
      onClose={onClose}
      onSubmit={() => {
        onSubmit &&
          onSubmit(
            findDifferentContent(
              originalState,
              contentEntitypublishedStateInput
            )
          );
      }}
      withScroll={true}
      handleAlert={false}
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
    >
      <div className=" space-y-4">
        {collectionStatus === 'loading' || parentStatus === 'loading' ? (
          <div
            className={`flex flex-col justify-center items-center space-y-2 font-medium`}
          >
            <ReturnTranslatedText
              origintext="Loading"
              text={`general.${'Loading'}`}
            />
          </div>
        ) : Object.keys(contentEntitypublishedStateInput).length ? (
          Object.keys(contentEntitypublishedStateInput).map((item) => (
            <div key={item}>
              <div
                className={`flex flex-col justify-center items-start space-y-2`}
              >
                <CheckBox
                  name={item}
                  checked={contentEntitypublishedStateInput[item].every(
                    (item) => item.published
                  )}
                  onClick={(val) => {
                    handleSelectAll(item, val);
                  }}
                  label={item}
                />
                {contentEntitypublishedStateInput[item].map((ele) => (
                  <div key={ele.contentEntityId} className=" ms-5">
                    <CheckBox
                      name={ele.contentEntityId}
                      checked={ele.published}
                      onClick={() => {
                        handleSelect(
                          item,
                          contentEntitypublishedStateInput[item].indexOf(ele),
                          !ele.published
                        );
                      }}
                      label={ele.title}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div
            className={`flex flex-col text-center justify-center items-start space-y-2`}
          >
            <ReturnTranslatedText
              origintext="There is no data to publish"
              text={`general.${'There is no data to publish'}`}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

function findDifferentContent(
  originalObject: { [key: string]: ContentEntitypublishedStateOutput[] },
  currentObject: { [key: string]: ContentEntitypublishedStateOutput[] }
): { [key: string]: ContentEntitypublishedStateOutput[] } {
  const differentContentObj: {
    [key: string]: ContentEntitypublishedStateOutput[];
  } = {};

  Object.keys(currentObject).forEach((key) => {
    const differentContent = currentObject[key].filter((item) =>
      originalObject[key].find(
        (ele) =>
          ele.contentEntityId === item.contentEntityId &&
          ele.published !== item.published
      )
    );

    if (differentContent.length) {
      differentContentObj[key] = differentContent;
    }
  });

  return differentContentObj;
}

export default PublishAllCollectionComponentsModal;
